/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InfiniteData,
  InvalidateQueryFilters,
  QueryClient,
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useQuery,
  UseQueryResult,
  useSuspenseInfiniteQuery,
  UseSuspenseInfiniteQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListOrganizationRemoteSessionClientsRequest,
  ListOrganizationRemoteSessionClientsSecurity,
} from "../models/operations/listorganizationremotesessionclients.js";
import { useGramContext } from "./_context.js";
import {
  InfiniteQueryHookOptions,
  QueryHookOptions,
  SuspenseInfiniteQueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildOrganizationRemoteSessionClientsInfiniteQuery,
  buildOrganizationRemoteSessionClientsQuery,
  OrganizationRemoteSessionClientsInfiniteQueryData,
  OrganizationRemoteSessionClientsPageParams,
  OrganizationRemoteSessionClientsQueryData,
  prefetchOrganizationRemoteSessionClients,
  prefetchOrganizationRemoteSessionClientsInfinite,
  queryKeyOrganizationRemoteSessionClients,
  queryKeyOrganizationRemoteSessionClientsInfinite,
} from "./organizationRemoteSessionClients.core.js";
export {
  buildOrganizationRemoteSessionClientsInfiniteQuery,
  buildOrganizationRemoteSessionClientsQuery,
  type OrganizationRemoteSessionClientsInfiniteQueryData,
  type OrganizationRemoteSessionClientsPageParams,
  type OrganizationRemoteSessionClientsQueryData,
  prefetchOrganizationRemoteSessionClients,
  prefetchOrganizationRemoteSessionClientsInfinite,
  queryKeyOrganizationRemoteSessionClients,
  queryKeyOrganizationRemoteSessionClientsInfinite,
};

export type OrganizationRemoteSessionClientsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listClients organizationRemoteSessionClients
 *
 * @remarks
 * List the remote_session_clients registered with a given issuer in the caller's organization, each with its MCP server attachment count. Requires org:read.
 */
export function useOrganizationRemoteSessionClients(
  request: ListOrganizationRemoteSessionClientsRequest,
  security?: ListOrganizationRemoteSessionClientsSecurity | undefined,
  options?: QueryHookOptions<
    OrganizationRemoteSessionClientsQueryData,
    OrganizationRemoteSessionClientsQueryError
  >,
): UseQueryResult<
  OrganizationRemoteSessionClientsQueryData,
  OrganizationRemoteSessionClientsQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildOrganizationRemoteSessionClientsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listClients organizationRemoteSessionClients
 *
 * @remarks
 * List the remote_session_clients registered with a given issuer in the caller's organization, each with its MCP server attachment count. Requires org:read.
 */
export function useOrganizationRemoteSessionClientsSuspense(
  request: ListOrganizationRemoteSessionClientsRequest,
  security?: ListOrganizationRemoteSessionClientsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    OrganizationRemoteSessionClientsQueryData,
    OrganizationRemoteSessionClientsQueryError
  >,
): UseSuspenseQueryResult<
  OrganizationRemoteSessionClientsQueryData,
  OrganizationRemoteSessionClientsQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildOrganizationRemoteSessionClientsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listClients organizationRemoteSessionClients
 *
 * @remarks
 * List the remote_session_clients registered with a given issuer in the caller's organization, each with its MCP server attachment count. Requires org:read.
 */
export function useOrganizationRemoteSessionClientsInfinite(
  request: ListOrganizationRemoteSessionClientsRequest,
  security?: ListOrganizationRemoteSessionClientsSecurity | undefined,
  options?: InfiniteQueryHookOptions<
    OrganizationRemoteSessionClientsInfiniteQueryData,
    OrganizationRemoteSessionClientsQueryError
  >,
): UseInfiniteQueryResult<
  InfiniteData<
    OrganizationRemoteSessionClientsInfiniteQueryData,
    OrganizationRemoteSessionClientsPageParams
  >,
  OrganizationRemoteSessionClientsQueryError
> {
  const client = useGramContext();
  return useInfiniteQuery<
    OrganizationRemoteSessionClientsInfiniteQueryData,
    OrganizationRemoteSessionClientsQueryError,
    InfiniteData<
      OrganizationRemoteSessionClientsInfiniteQueryData,
      OrganizationRemoteSessionClientsPageParams
    >,
    QueryKey,
    OrganizationRemoteSessionClientsPageParams
  >({
    ...buildOrganizationRemoteSessionClientsInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

/**
 * listClients organizationRemoteSessionClients
 *
 * @remarks
 * List the remote_session_clients registered with a given issuer in the caller's organization, each with its MCP server attachment count. Requires org:read.
 */
export function useOrganizationRemoteSessionClientsInfiniteSuspense(
  request: ListOrganizationRemoteSessionClientsRequest,
  security?: ListOrganizationRemoteSessionClientsSecurity | undefined,
  options?: SuspenseInfiniteQueryHookOptions<
    OrganizationRemoteSessionClientsInfiniteQueryData,
    OrganizationRemoteSessionClientsQueryError
  >,
): UseSuspenseInfiniteQueryResult<
  InfiniteData<
    OrganizationRemoteSessionClientsInfiniteQueryData,
    OrganizationRemoteSessionClientsPageParams
  >,
  OrganizationRemoteSessionClientsQueryError
> {
  const client = useGramContext();
  return useSuspenseInfiniteQuery<
    OrganizationRemoteSessionClientsInfiniteQueryData,
    OrganizationRemoteSessionClientsQueryError,
    InfiniteData<
      OrganizationRemoteSessionClientsInfiniteQueryData,
      OrganizationRemoteSessionClientsPageParams
    >,
    QueryKey,
    OrganizationRemoteSessionClientsPageParams
  >({
    ...buildOrganizationRemoteSessionClientsInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

export function setOrganizationRemoteSessionClientsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      issuerId: string;
      cursor?: string | undefined;
      limit?: number | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
    },
  ],
  data: OrganizationRemoteSessionClientsQueryData,
): OrganizationRemoteSessionClientsQueryData | undefined {
  const key = queryKeyOrganizationRemoteSessionClients(...queryKeyBase);

  return client.setQueryData<OrganizationRemoteSessionClientsQueryData>(
    key,
    data,
  );
}

export function invalidateOrganizationRemoteSessionClients(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      issuerId: string;
      cursor?: string | undefined;
      limit?: number | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "organizationRemoteSessionClients",
      "list",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllOrganizationRemoteSessionClients(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "organizationRemoteSessionClients", "list"],
  });
}
